import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

import type { ProductionState } from '@/hooks/useProduction';

function formatEta(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 60) return `~${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `~${h}h ${m}m` : `~${h}h`;
}

/**
 * Live ETA for the phase currently running, projected from ITS OWN item rate
 * (storyboard frames now, clips next, …). Appears only once ≥1 item has completed
 * since we began observing, so there's a real rate behind it; it self-corrects on
 * every shot_progress event and counts down between them.
 */
export default function PhaseEta({
  phaseTiming,
  running,
  compact = false,
}: {
  phaseTiming: ProductionState['phaseTiming'];
  running?: boolean;
  compact?: boolean;
}) {
  const { t } = useTranslation('production');
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, [running]);

  if (!running || !phaseTiming) return null;
  const { step, startedTs, startCurrent, current, total, updatedTs, updatedAt } = phaseTiming;
  const completed = current - startCurrent; // items finished since we began observing
  const remainingItems = Math.max(0, total - current); // items AFTER the in-progress one
  if (completed < 1 || total <= 0) return null; // not enough data to estimate yet

  // Rate uses SERVER emit times: a burst replay on (re)connect arrives within
  // milliseconds of the client clock, so measuring the client wall-clock span
  // collapsed perItem toward 0 and the ETA read "<1 min" with a full board left.
  const observed = updatedTs - startedTs;
  if (observed <= 0) return null; // only one real timestamp so far — no rate yet
  const perItem = observed / completed;

  // Remaining = time to finish the in-progress frame + the not-yet-started ones.
  // The current frame can DRAG (identity-gate retries), so floor its remaining at 0
  // rather than subtracting the overrun from the whole estimate — that's what used
  // to drive the countdown negative into "any moment" while work remained.
  const currentElapsed = Date.now() - updatedAt;
  const currentRemaining = Math.max(perItem - currentElapsed, 0);
  const projected = currentRemaining + remainingItems * perItem;
  const label = t(`step.${step === 'shooting' ? 'dailies' : step}`, { defaultValue: step });
  // Last frame overrunning its average by a lot — a real ETA would be a guess.
  const overdue = currentElapsed > perItem * 2;
  const etaText = remainingItems === 0 && overdue ? t('eta.wrappingUp') : formatEta(projected);

  if (compact) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" />
        {t('eta.label', { phase: label })} {etaText}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-muted-foreground">{t('eta.label', { phase: label })}</span>
      <span className="font-medium">{etaText}</span>
      <span className="ml-auto text-xs text-faint">{t('eta.left', { count: remainingItems })}</span>
    </div>
  );
}
